import { STAFF_ROLES, type StaffUserDto } from '@deli/contracts';
import { useState, type FormEvent } from 'react';
import { useCreateUser, useSetPassword, useUpdateUser, useUsers } from '../api/staff.js';
import { useCurrentUser } from '../auth/session.js';
import { ErrorText } from '../components/ErrorText.js';
import { QueryState } from '../components/QueryState.js';
import { formatDateTime } from '../format.js';
import { t } from '../i18n/uk.js';

type Role = (typeof STAFF_ROLES)[number];

function RoleSelect({
  value,
  onChange,
  label,
}: {
  value: Role;
  onChange: (r: Role) => void;
  label: string;
}) {
  return (
    <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value as Role)}>
      {STAFF_ROLES.map((role) => (
        <option key={role} value={role}>
          {t.roles[role]}
        </option>
      ))}
    </select>
  );
}

function CreateUserForm() {
  const create = useCreateUser();
  const [form, setForm] = useState({ name: '', email: '', role: 'manager' as Role, password: '' });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(form, {
      onSuccess: () => setForm({ name: '', email: '', role: 'manager', password: '' }),
    });
  };
  return (
    <form className="card inline-form" onSubmit={submit}>
      <h2>{t.users.add}</h2>
      <input
        aria-label={t.users.name}
        placeholder={t.users.name}
        required
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
      />
      <input
        aria-label={t.users.email}
        placeholder={t.users.email}
        type="email"
        required
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
      />
      <RoleSelect
        label={t.users.role}
        value={form.role}
        onChange={(role) => setForm({ ...form, role })}
      />
      <input
        aria-label={t.users.initialPassword}
        placeholder={t.users.initialPassword}
        type="password"
        autoComplete="new-password"
        required
        value={form.password}
        onChange={(e) => setForm({ ...form, password: e.target.value })}
      />
      <button type="submit" className="primary" disabled={create.isPending}>
        {t.users.create}
      </button>
      <ErrorText error={create.error} />
    </form>
  );
}

function UserRow({ user, isMe }: { user: StaffUserDto; isMe: boolean }) {
  const update = useUpdateUser();
  const setPassword = useSetPassword();
  const [password, setNewPassword] = useState('');
  const savePassword = (event: FormEvent) => {
    event.preventDefault();
    setPassword.mutate({ id: user.id, password }, { onSuccess: () => setNewPassword('') });
  };
  return (
    <tr>
      <td>{user.name}</td>
      <td>{user.email}</td>
      <td>
        <RoleSelect
          label={`${t.users.role}: ${user.email}`}
          value={user.role}
          onChange={(role) => update.mutate({ id: user.id, role })}
        />
      </td>
      <td>
        <span className={user.isActive ? 'badge ok' : 'badge bad'}>
          {user.isActive ? t.users.active : t.users.inactive}
        </span>
        {user.lockedUntil && (
          <div className="muted">
            {t.users.locked} {formatDateTime(user.lockedUntil)}
          </div>
        )}
      </td>
      <td className="actions">
        {!isMe && (
          <button
            type="button"
            onClick={() => update.mutate({ id: user.id, isActive: !user.isActive })}
          >
            {user.isActive ? t.users.deactivate : t.users.activate}
          </button>
        )}
        <form className="inline" onSubmit={savePassword}>
          <input
            aria-label={`${t.users.newPassword}: ${user.email}`}
            placeholder={t.users.newPassword}
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <button type="submit">{t.users.setPassword}</button>
        </form>
        {setPassword.isSuccess && <p className="muted">{t.users.passwordSet}</p>}
        <ErrorText error={update.error ?? setPassword.error} />
      </td>
    </tr>
  );
}

export function UsersPage() {
  const users = useUsers();
  const me = useCurrentUser();
  return (
    <section>
      <h1>{t.users.title}</h1>
      <CreateUserForm />
      <QueryState query={users}>
        {(list) => (
          <table className="table">
            <thead>
              <tr>
                <th>{t.users.name}</th>
                <th>{t.users.email}</th>
                <th>{t.users.role}</th>
                <th>{t.users.status}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((user) => (
                <UserRow key={user.id} user={user} isMe={user.id === me.id} />
              ))}
            </tbody>
          </table>
        )}
      </QueryState>
    </section>
  );
}
